import {useEffect, useRef, useState} from 'react'
import {useMatch, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'

import {Modal} from '../../../../../../_metronic/elements/Modal'
import {InputText} from '../../../../../../_metronic/elements/Inputs'
import {useViewLandContext} from '../ViewLand/ViewLandContext'
import {useViewImageContext} from '../ViewLand/Images/ViewImageContext'
import {useErrorHandler} from '../../../../../../_metronic/helpers/ErrorHandler'
import {openAlert} from '../../../../../../_metronic/elements/NotificationAlert'
import {useMapLayersV2} from '../../../Map/MapLayersV2Context'
import {useDrawV2} from '../../../Map/Draw/DrawV2Context'
import {useDrawEndButton} from '../../../Map/Draw/DrawEndButton/DrawEndButtonContext'
import {DrawTools} from '../../../Map/Draw/DrawState'
import * as GisDataAdapter from '../../services/GisDataAdapter'
import {useSearchContext} from '../../../Toolbar/SearchContext'
import {useViewFieldContext} from '../ViewFieldContext'
import {useIsMobile} from '../../../../../shared/responsive'
import '../../CreateFlow.style.scss'
import PlanetHdPerimeterConfirmModal, {PlanetHdAccessCheck} from '../PlanetHdLandChangeWarning'
import {
  HD_ACCESS_CHECKING,
  HD_ACCESS_ERROR,
  PLANET_HD_VISUALIZATION_ACCESS_PARAMS,
  needsPlanetHdConfirmation,
  resolveHdAccessCheck,
} from '../planetHdLandChange'

const ModalHeader = () => {
  const {t} = useTranslation()

  return (
    <div className='modal-header p-5'>
      <h5 className='modal-title'>{t('lands:update.title')}</h5>
    </div>
  )
}

const ModalBody = ({
  hasAmbient,
  hdAccessStatus,
  onRetryHdAccess,
  polygonUpdated,
  updatePolygon,
  formik,
  loading,
}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-body p-5'>
      <div className='row'>
        <div className='col-lg-12 fv-row'>
          <InputText
            disabled={loading}
            formik={formik}
            name='name'
            inputProps={{
              placeholder: 'Nombre',
            }}
          />
        </div>
        <div className='col-lg-12'>
          <button
            onClick={updatePolygon}
            type='button'
            disabled={
              loading || hdAccessStatus === HD_ACCESS_CHECKING || hdAccessStatus === HD_ACCESS_ERROR
            }
            className={`btn ${polygonUpdated ? 'btn-light-success' : 'btn-light-primary'} mt-5`}
            style={{width: '100%'}}
          >
            {t('lands:update.buttons.draw')}
          </button>
          {(hdAccessStatus === HD_ACCESS_CHECKING || hdAccessStatus === HD_ACCESS_ERROR) && (
            <div className='mt-3'>
              <PlanetHdAccessCheck status={hdAccessStatus} onRetry={onRetryHdAccess} />
            </div>
          )}
          {hasAmbient && (
            <div className='alert alert-primary d-flex align-items-center p-5 mt-5'>
              <div className='d-flex flex-column'>
                <h5 className='mb-1'>{t('lands:update.ambient_alert.title')}</h5>
                <span>{t('lands:update.ambient_alert.message')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const ModalFooter = ({loading, cancel}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-footer'>
      <button disabled={loading === true} type='button' className='btn btn-light' onClick={cancel}>
        {t('lands:update.buttons.cancel')}
      </button>
      <button type='submit' className='btn btn-primary'>
        {!loading && t('lands:update.buttons.update')}
        {loading && (
          <span className='indicator-progress' style={{display: 'block'}}>
            {t('lands:update.updating')}{' '}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        )}
      </button>
    </div>
  )
}

const MobileUpdateLandCard = ({
  land,
  formik,
  loading,
  hasAmbient,
  hdAccessStatus,
  onRetryHdAccess,
  polygonUpdated,
  updatePolygon,
  cancel,
}) => {
  const {t} = useTranslation()

  return (
    <div className='view-land-mobile-shell displayable'>
      <form onSubmit={formik.handleSubmit} className='form'>
        <div className='card shadow-sm view-land-container right-card-data gis-create-flow-card'>
          <div className='card-header'>
            <p className='gis-create-flow-eyebrow'>{t('fields:labels.land')}</p>
            <h4 className='gis-create-flow-title'>{t('lands:update.title')}</h4>
          </div>

          <div className='card-body'>
            <div className='gis-create-flow-block'>
              <div className='gis-create-flow-input'>
                <p className='gis-create-flow-label'>{t('lands:update.mobile.edit.name_label')}</p>
                <InputText
                  disabled={loading}
                  formik={formik}
                  name='name'
                  inputProps={{
                    placeholder: t('lands:update.mobile.edit.name_placeholder'),
                  }}
                />
              </div>

              <button
                onClick={updatePolygon}
                type='button'
                disabled={
                  loading ||
                  hdAccessStatus === HD_ACCESS_CHECKING ||
                  hdAccessStatus === HD_ACCESS_ERROR
                }
                className={`btn ${
                  polygonUpdated ? 'btn-light-success' : 'btn-light-primary'
                } w-100`}
              >
                {t('lands:update.buttons.draw')}
              </button>

              <PlanetHdAccessCheck status={hdAccessStatus} onRetry={onRetryHdAccess} />

              {hasAmbient && (
                <div className='gis-create-flow-hint gis-create-flow-hint--warning'>
                  <p className='gis-create-flow-hint__title'>
                    {t('lands:update.ambient_alert.title')}
                  </p>
                  <p className='gis-create-flow-hint__copy'>
                    {t('lands:update.ambient_alert.message')}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className='card-footer'>
            <button
              disabled={loading === true}
              type='button'
              className='btn btn-light gis-create-flow-footer-button'
              onClick={cancel}
            >
              {t('lands:update.buttons.cancel')}
            </button>
            <button type='submit' className='btn btn-primary gis-create-flow-footer-button'>
              {!loading && t('lands:update.buttons.update')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {t('lands:update.updating')}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

const UpdateLand = ({open, setOpen}) => {
  const {land, setLand} = useViewLandContext()
  const viewImageContext = useViewImageContext()
  const [polygonUpdated, setPolygonUpdated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [polygon, setPolygon] = useState(null)
  const [hasAmbient, setHasAmbient] = useState(false)
  const [hdAccessStatus, setHdAccessStatus] = useState(HD_ACCESS_CHECKING)
  const [hdAccessCheckVersion, setHdAccessCheckVersion] = useState(0)
  const [showHdPerimeterConfirmation, setShowHdPerimeterConfirmation] = useState(false)
  const {field_id, land_id} = useParams()
  const {errorHandler} = useErrorHandler()
  const errorHandlerRef = useRef(errorHandler)
  const {t} = useTranslation()
  const viewFieldContext = useViewFieldContext()
  const search = useSearchContext()
  const drawV2 = useDrawV2()
  const drawEndButton = useDrawEndButton()
  const mapLayers = useMapLayersV2()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const routeMatch = useMatch('/gis/field/view/:field_id/land/view/:land_id/update')
  const isRouteDriven = isMobile && routeMatch != null
  const isEditorOpen = isRouteDriven ? !drawV2.isDrawing : open

  errorHandlerRef.current = errorHandler

  const schema = Yup.object().shape({
    name: Yup.string()
      .min(1, t('lands:update.form.input.name.errors.min'))
      .max(32, t('lands:update.form.input.name.errors.max'))
      .required(t('lands:update.form.input.name.errors.required')),
  })

  const formik = useFormik({
    initialValues: {
      name: land?.name || '',
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true)
      const extra = {}

      if (polygon?.geometry) {
        extra.polygon = polygon.geometry
      }

      const res = await errorHandler(
        GisDataAdapter.updateLand(land.land_id, {name: values.name, ...extra})
      )

      if (res.ok) {
        if (polygon?.geometry) {
          viewImageContext.invalidateLandGeometry(res.data)
        }
        mapLayers.reloadSource()
        search.load()
        viewFieldContext.loadLands(field_id)
        setLand(res.data)
        openAlert('success', t('lands:update.success.title'), t('lands:update.success.message'))
        cancel()
        navigate(`/gis/field/view/${field_id}/land/view/${land_id}`)
      }

      setLoading(false)
    },
  })

  useEffect(() => {
    setPolygonUpdated(Boolean(polygon?.geometry))

    if (!isEditorOpen || !land?.land_id) {
      return
    }

    let active = true

    errorHandlerRef.current(GisDataAdapter.getLandAmbient(land.land_id), [404]).then((res) => {
      if (!active) {
        return
      }

      setHasAmbient(res.ok)
    })

    setHdAccessStatus(HD_ACCESS_CHECKING)
    errorHandlerRef
      .current(
        GisDataAdapter.getLandHdAccess(land.land_id, PLANET_HD_VISUALIZATION_ACCESS_PARAMS),
        [404, 500]
      )
      .then((res) => {
        if (!active) {
          return
        }

        setHdAccessStatus(resolveHdAccessCheck(res))
      })

    return () => {
      active = false
    }
  }, [hdAccessCheckVersion, isEditorOpen, land?.land_id, polygon?.geometry])

  const reopenEditor = (nextPolygon) => {
    setPolygon(nextPolygon)
    setPolygonUpdated(Boolean(nextPolygon?.geometry))

    if (!isRouteDriven) {
      setOpen(true)
    }
  }

  const beginPolygonUpdate = () => {
    setPolygonUpdated(true)
    mapLayers.land.hide(land.land_id)

    let polygonId = polygon?.id

    if (!polygonId) {
      polygonId = drawV2.add(land.polygons || land.polygon)
      setPolygon({
        id: polygonId,
        geometry: land.polygons || land.polygon,
      })
      mapLayers.fit(land.land_id)
    }

    drawV2.startDrawing({
      tools: [DrawTools.POLYGON],
    })
    drawV2.directSelect(polygonId)

    if (!isRouteDriven) {
      setOpen(false)
    }

    openAlert('info', t('lands:update.draw.title'), t('lands:update.draw.message'))

    drawEndButton.show({
      title: t('lands:update.draw.drawed'),
      buttonText: t('lands:update.draw.end_button'),
      onFinish: () => {
        const updatedFeature = drawV2.get(polygonId)
        drawEndButton.hide()
        drawV2.endDrawing()

        if (!updatedFeature?.geometry) {
          reopenEditor(null)
          return
        }

        reopenEditor({
          id: polygonId,
          geometry: updatedFeature.geometry,
        })
      },
      onCancel: () => {
        drawEndButton.hide()
        drawV2.endDrawing()
        setPolygon(null)
        setPolygonUpdated(false)
        mapLayers.land.unfill(land.land_id)

        if (!isRouteDriven) {
          setOpen(true)
        }
      },
    })
  }

  const updatePolygon = () => {
    if (hdAccessStatus === HD_ACCESS_CHECKING || hdAccessStatus === HD_ACCESS_ERROR) {
      return
    }

    if (needsPlanetHdConfirmation(hdAccessStatus)) {
      setShowHdPerimeterConfirmation(true)
      return
    }

    beginPolygonUpdate()
  }

  const confirmHdPerimeterUpdate = () => {
    setShowHdPerimeterConfirmation(false)
    beginPolygonUpdate()
  }

  const retryHdAccessCheck = () => {
    setHdAccessCheckVersion((current) => current + 1)
  }

  const onSetOpen = () => {
    return undefined
  }

  const cancel = () => {
    setPolygonUpdated(false)
    setPolygon(null)
    setHasAmbient(false)
    setShowHdPerimeterConfirmation(false)
    mapLayers.land.unfill(land.land_id)
    drawEndButton.hide()
    drawV2.endDrawing()
    drawV2.deleteAllLayers()
    formik.resetForm()
    if (isRouteDriven) {
      navigate(`/gis/field/view/${field_id}/land/view/${land_id}`)
      return
    }

    setOpen(false)
  }

  useEffect(() => {
    if (polygon?.geometry) {
      setPolygonUpdated(true)
      return
    }

    setPolygonUpdated(false)
  }, [polygon])

  if (isMobile) {
    if (!isEditorOpen) {
      return null
    }

    return (
      <>
        <MobileUpdateLandCard
          land={land}
          formik={formik}
          loading={loading}
          hasAmbient={hasAmbient}
          hdAccessStatus={hdAccessStatus}
          onRetryHdAccess={retryHdAccessCheck}
          polygonUpdated={polygonUpdated}
          updatePolygon={updatePolygon}
          cancel={cancel}
        />
        <PlanetHdPerimeterConfirmModal
          open={showHdPerimeterConfirmation}
          onCancel={() => setShowHdPerimeterConfirmation(false)}
          onConfirm={confirmHdPerimeterUpdate}
        />
      </>
    )
  }

  return (
    <>
      <Modal open={open} setOpen={onSetOpen}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <form onSubmit={formik.handleSubmit} className='form'>
              <ModalHeader />
              <ModalBody
                hasAmbient={hasAmbient}
                hdAccessStatus={hdAccessStatus}
                onRetryHdAccess={retryHdAccessCheck}
                polygonUpdated={polygonUpdated}
                updatePolygon={updatePolygon}
                formik={formik}
                loading={loading}
              />
              <ModalFooter cancel={cancel} loading={loading} />
            </form>
          </div>
        </div>
      </Modal>
      <PlanetHdPerimeterConfirmModal
        open={showHdPerimeterConfirmation}
        onCancel={() => setShowHdPerimeterConfirmation(false)}
        onConfirm={confirmHdPerimeterUpdate}
      />
    </>
  )
}

export default UpdateLand
